import Column from '@app/components/layout/Column'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { useParamState } from '@app/util/hooks/useParamState'
import { useQuery } from '@based/sdk/react'
import { Overview } from './tabContent/Overview'
import { Initiatives } from './tabContent/Initiatives'
import { Current } from './tabContent/Current'
import { Thread } from '@app/components/blocks/thread/Thread'
import { Locations } from './tabContent/Locations'
import { StepByStep } from './tabContent/StepByStep'
import { DevelopmentAreas } from './tabContent/DevelopmentAreas'
import { Subsidies } from './tabContent/Subsidies'
import { MunicipalityViewModel } from 'functions/app/geography/view/municipality'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { isFeatureEnabled } from '@app/util/featureToggle'
import { regionsTabs, regionsTabsQueryParamName } from '../constants'
import { RegionalServiceProvidersGrid } from './tabContent/RegionalServiceProvidersGrid'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'

type Tab = keyof typeof regionsTabs

const municipalityTabKeys: Tab[] = [
  regionsTabs.overview,
  regionsTabs.discussion,
  regionsTabs.initiatives,
  regionsTabs.locations,
  regionsTabs.knowledgeHub,
  regionsTabs.experts,
]

const isTabAvailable = (
  municipality: MunicipalityViewModel,
  tab: Tab,
): boolean => {
  return match(tab)
    .with(regionsTabs.overview, () => true)
    .with(regionsTabs.discussion, () => true)
    .with(regionsTabs.initiatives, () => true)
    .with(regionsTabs.locations, () => true)
    .with(
      regionsTabs.knowledgeHub,
      (matchedTab) =>
        (municipality.knowledgeHubModules?.length ?? 0) > 0 &&
        isFeatureEnabled(municipality.featureToggles, matchedTab),
    )
    .with(
      regionsTabs.experts,
      (matchedTab) =>
        (municipality.serviceProviders?.count ?? 0) > 0 &&
        isFeatureEnabled(municipality.featureToggles, matchedTab),
    )
    .otherwise((matchedTab) =>
      isFeatureEnabled(municipality.featureToggles, matchedTab),
    )
}

export const MunicipalityTabContent = ({
  municipality,
}: {
  municipality: MunicipalityViewModel
}) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.regionPage
  const [tab, setTab] = useParamState(
    regionsTabs,
    regionsTabs.overview,
    regionsTabsQueryParamName,
  )

  const isExpertsFeatureEnabled = isFeatureEnabled(
    municipality.featureToggles,
    regionsTabs.experts,
  )
  const regionalExpertsCountQuery = useQuery(
    isExpertsFeatureEnabled ? 'list-regional-service-providers' : null,
    {
      municipalityId: municipality.id,
      count: true,
    },
  )
  const expertsCount = regionalExpertsCountQuery.data?.count ?? 0
  const showExpertsTab = isExpertsFeatureEnabled && expertsCount > 0

  const regionalExpertsListQuery = useQuery(
    tab === regionsTabs.experts && showExpertsTab
      ? 'list-regional-service-providers'
      : null,
    {
      municipalityId: municipality.id,
    },
  )
  const regionalExperts: ServiceProviderListRegionalModel[] =
    regionalExpertsListQuery.data !== undefined
      ? (regionalExpertsListQuery.data as ServiceProviderListRegionalModel[])
      : []

  const getTabLabel = (tabKey: Tab): string => translations[tabKey]

  return (
    <Column alignItems="stretch" gap="2rem">
      <TabsGroup
        selectedTab={tab}
        onSelectTab={setTab}
        tabs={municipalityTabKeys.map((key) => ({
          value: key,
          label: getTabLabel(key),
          isVisible:
            key === regionsTabs.experts
              ? showExpertsTab
              : isTabAvailable(municipality, key),
        }))}
      />
      {match(tab)
        .with(regionsTabs.overview, () => (
          <Overview key="overview" entity={municipality} />
        ))
        .with(regionsTabs.discussion, () => (
          <Thread key="thread" threadId={municipality.thread?.id} />
        ))
        .with(regionsTabs.initiatives, () => (
          <Initiatives key="initiatives" entity={municipality} />
        ))
        .with(regionsTabs.locations, () => (
          <Locations key="locations" entity={municipality} />
        ))
        .with(regionsTabs.knowledgeHub, (t) =>
          isTabAvailable(municipality, t) ? (
            <KnowledgeHubModulesSection
              key="knowledgeHub"
              modules={municipality.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .with(regionsTabs.experts, () =>
          showExpertsTab ? (
            <RegionalServiceProvidersGrid
              key="experts"
              serviceProviders={regionalExperts}
            />
          ) : null,
        )
        .with(regionsTabs.current, () => (
          <Current key="current" region={municipality} />
        ))
        .with(regionsTabs.developmentAreas, () => (
          <DevelopmentAreas key="developmentAreas" region={municipality} />
        ))
        .with(regionsTabs.stepByStep, () => (
          <StepByStep key="stepByStep" region={municipality} />
        ))
        .with(regionsTabs.subsidies, () => (
          <Subsidies key="subsidies" region={municipality} />
        ))
        .exhaustive()}
    </Column>
  )
}
